import { type ClientFrame, HEARTBEAT_INTERVAL_MS, type ServerFrame } from '@feral/shared/client'
import { onSessionEnd } from './session.js'

type Listener = (f: ServerFrame) => void

/**
 * One socket per client (architecture §9.1). Heartbeats every 45 s while the page is visible, re-sends
 * the viewport after a reconnect, and backs off when the server is away. Frames are typed from the
 * shared schemas; nothing here interprets them.
 */
class Socket {
  private ws: WebSocket | null = null
  private listeners = new Set<Listener>()
  private cells: string[] = []
  private wall = false
  private hb: number | null = null
  private retry = 0
  private wanted = false
  private timer: number | null = null

  connect() {
    this.wanted = true
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING))
      return
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const ws = new WebSocket(`${proto}://${location.host}/v1/ws`)
    this.ws = ws
    ws.onopen = () => {
      this.retry = 0
      if (this.cells.length) this.send({ t: 'viewport', cells: this.cells })
      if (this.wall) this.send({ t: 'wall', on: true })
      this.startHeartbeat()
    }
    ws.onmessage = (e) => {
      try {
        const f = JSON.parse(e.data as string) as ServerFrame
        for (const l of this.listeners) l(f)
      } catch {}
    }
    ws.onclose = (e) => {
      this.stopHeartbeat()
      this.ws = null
      if (!this.wanted || e.code === 4001) return
      // Doubling from a second, capped at a minute, each try spread over half to one and a half times
      // that: a deploy closes every socket in the same second, and without the spread every phone came
      // back in the same second too (docs/PERFORMANCE.md PF-02).
      const base = Math.min(60_000, 1000 * 2 ** this.retry++)
      const delay = Math.round(base * (0.5 + Math.random()))
      this.timer = window.setTimeout(() => this.connect(), delay)
    }
    ws.onerror = () => ws.close()
  }

  /** After a sign-out: closed, and nothing of the last account's view kept for the next. */
  reset() {
    this.disconnect()
    this.cells = []
    this.wall = false
  }

  /** Back to a visible tab: reconnect only if a signed-in screen wanted the socket. */
  resume() {
    if (this.wanted) this.connect()
  }

  disconnect() {
    this.wanted = false
    if (this.timer) window.clearTimeout(this.timer)
    this.stopHeartbeat()
    this.ws?.close()
    this.ws = null
  }

  /** The cells the map is showing; the server fans `pin.update` out per cell (architecture §9.2). */
  setViewport(cells: string[]) {
    const same = cells.length === this.cells.length && cells.every((c, i) => c === this.cells[i])
    this.cells = cells
    if (!same) this.send({ t: 'viewport', cells })
  }

  /**
   * Hear the Wall around your pin (FR-WAL-01, 12). Call again after the pin moves: the server works out
   * the channels from where you are now.
   */
  watchWall() {
    this.wall = true
    this.send({ t: 'wall', on: true })
  }

  /** Tell the other side you are typing; the server relays it to them only. */
  sendTyping(conversationId: string) {
    this.send({ t: 'typing', conversationId })
  }

  on(l: Listener) {
    this.listeners.add(l)
    return () => {
      this.listeners.delete(l)
    }
  }

  private send(f: ClientFrame) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(f))
  }
  private startHeartbeat() {
    this.stopHeartbeat()
    this.hb = window.setInterval(() => {
      if (document.visibilityState === 'visible') this.send({ t: 'hb' })
    }, HEARTBEAT_INTERVAL_MS)
  }
  private stopHeartbeat() {
    if (this.hb) window.clearInterval(this.hb)
    this.hb = null
  }
}

export const socket = new Socket()

document.addEventListener('visibilitychange', () => {
  // Coming back to the tab: make sure the socket is up and send a heartbeat straight away.
  if (document.visibilityState === 'visible') socket.resume()
})
// Signing out closes the socket (client review #9); the server also closes it (review #3).
onSessionEnd(() => socket.reset())
